import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

/**
 * Layout variants for the application.
 */
export const LayoutVariant = {
    DEFAULT: 'default',
    MINIMAL: 'minimal'
} as const;
/**
 * @ignore
 */
export type LayoutVariant = ( typeof LayoutVariant )[ keyof typeof LayoutVariant ];
/**
 * @ignore
 */
export type LayoutRouteData = Record<string, unknown> & { layout?: LayoutVariant };
/**
 * Checks if a given value is a valid layout variant.
 * @param value The value to check.
 * @returns True if the value is a valid layout variant, false otherwise.
 */
const isLayoutVariant = ( value: unknown ): value is LayoutVariant =>
    Object.values( LayoutVariant ).some( ( layout ) => layout === value );

/** layout service supplies a boolean value for a data flag
 * which is set as data attribute in a given route
 */
@Injectable( {
    providedIn: 'root'
} )
export class LayoutService {
    #router = inject( Router );

    readonly #activeLayout = toSignal(
        this.#router.events.pipe(
            filter( ( event ) => event instanceof NavigationEnd ),
            map( () => this.#resolveLayout( this.#router.routerState.snapshot.root ) )
        ),
        { initialValue: LayoutVariant.DEFAULT }
    );

    isLayout = ( layout: LayoutVariant = LayoutVariant.DEFAULT ) => computed( () => this.#activeLayout() === layout );

    #resolveLayout( root: ActivatedRouteSnapshot ) {
        let activeRoute: ActivatedRouteSnapshot | null = root;
        let resolvedLayout: LayoutVariant | undefined;

        // traverse the route tree to find the active layout
        while ( activeRoute ) {
            const routeLayout: unknown = activeRoute.data[ 'layout' ];
            if ( isLayoutVariant( routeLayout ) ) {
                resolvedLayout = routeLayout;
            }
            activeRoute = activeRoute.firstChild ?? null;
        }

        return resolvedLayout ?? LayoutVariant.DEFAULT;
    }
}
